import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Plus, Loader2, Send, CheckCircle, Truck, Package, XCircle, Warehouse, ArrowUpDown, SendHorizonal, ChevronDown, X } from 'lucide-react';
import { callOffAPI } from '../../../lib/api';
import type { CallOff, CallOffStats } from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useFormatters } from '../../../contexts/PreferencesContext';
import { Card, CardContent } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Checkbox } from '../../ui/checkbox';
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from '../../ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../ui/alert-dialog';
import { toast } from 'sonner';
import CallOffWizard from './CallOffWizard';
import CallOffDetailModal from './CallOffDetailModal';

const STATUS_CONFIG: Record<string, { icon: React.ComponentType<{ className?: string }>; color: string; label: string }> = {
  draft: { icon: Package, color: 'bg-gray-100 text-gray-700', label: 'Draft' },
  pending: { icon: Send, color: 'bg-blue-100 text-blue-700', label: 'Pending' },
  confirmed: { icon: CheckCircle, color: 'bg-green-100 text-green-700', label: 'Confirmed' },
  dispatched: { icon: Truck, color: 'bg-orange-100 text-orange-700', label: 'Dispatched' },
  delivered: { icon: Package, color: 'bg-emerald-100 text-emerald-700', label: 'Delivered' },
  cancelled: { icon: XCircle, color: 'bg-red-100 text-red-700', label: 'Cancelled' },
};

const DESTINATION_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  warehouse: Warehouse,
  direct: Truck,
  mixed: ArrowUpDown,
};

function getFillColorClass(fillPercent: number): string {
  if (fillPercent >= 90) return 'text-red-600 font-semibold';
  if (fillPercent >= 50) return 'text-orange-500 font-semibold';
  return 'text-green-600';
}

interface SupplierOption {
  id: number;
  name: string;
  supplier_code: string;
}

export default function CallOffManagementTab() {
  const { user } = useAuth();
  const { formatDate } = useFormatters();
  const [searchParams, setSearchParams] = useSearchParams();
  const isCustomer = user?.company_type === 'customer' || user?.platform === 'CUSTOMER';
  const [callOffs, setCallOffs] = useState<CallOff[]>([]);
  const [stats, setStats] = useState<CallOffStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState<'active' | 'completed' | 'cancelled'>('active');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [suppliers, setSuppliers] = useState<SupplierOption[]>([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [selectedCallOffId, setSelectedCallOffId] = useState<number | null>(() => {
    const urlId = searchParams.get('calloff_id');
    return urlId ? parseInt(urlId, 10) : null;
  });
  const [editCallOff, setEditCallOff] = useState<CallOff | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkAction, setBulkAction] = useState<'send' | 'cancel' | 'confirm' | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const pageSize = 20;

  // Searchable supplier dropdown state
  const [supplierSearchTerm, setSupplierSearchTerm] = useState('');
  const [supplierDropdownOpen, setSupplierDropdownOpen] = useState(false);

  const filteredSupplierOptions = useMemo(() => {
    const term = supplierSearchTerm.toLowerCase();
    if (!term) return suppliers;
    return suppliers.filter(s => s.name.toLowerCase().includes(term));
  }, [suppliers, supplierSearchTerm]);

  const selectedSupplierName = useMemo(() => {
    if (!supplierFilter) return '';
    const supplier = suppliers.find(s => String(s.id) === supplierFilter);
    return supplier ? `${supplier.name}${supplier.supplier_code ? ` (${supplier.supplier_code})` : ''}` : '';
  }, [supplierFilter, suppliers]);

  // Load suppliers for filter dropdown (customer only)
  useEffect(() => {
    if (!isCustomer) return;
    const loadSuppliers = async () => {
      try {
        const res = await callOffAPI.getSuppliers();
        setSuppliers(res.data || []);
      } catch {
        // Suppliers are optional for filtering
      }
    };
    loadSuppliers();
  }, [isCustomer]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, string | number | boolean> = {
        page,
        page_size: pageSize,
      };
      // Map the bucket toggle to the right backend filter:
      //   active    → status=active (covers draft/pending/confirmed/dispatched)
      //   completed → detail_status=delivered (only successfully delivered)
      //   cancelled → detail_status=cancelled
      if (activeFilter === 'active') {
        params['status'] = 'active';
      } else if (activeFilter === 'completed') {
        params['detail_status'] = 'delivered';
      } else {
        params['detail_status'] = 'cancelled';
      }
      // statusFilter (stat-card drill-in) overrides the toggle's detail_status default
      if (statusFilter) params['detail_status'] = statusFilter;
      if (search) params['search'] = search;
      if (supplierFilter) params['supplier'] = supplierFilter;

      const [listRes, statsRes] = await Promise.all([
        callOffAPI.getCallOffs(params),
        callOffAPI.getCallOffStats(),
      ]);

      setCallOffs(listRes.data.results || []);
      setTotalCount(listRes.data.count || 0);
      setStats(statsRes.data);
      setSelectedIds(new Set());
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load call-offs';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, activeFilter, supplierFilter, search]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Which call-offs can be bulk-selected?
  // Customer: draft call-offs; Supplier: pending call-offs
  const selectableStatus = isCustomer ? 'draft' : 'pending';
  const selectableCallOffs = callOffs.filter((co) => co.detail_status === selectableStatus);
  const allSelectableChecked = selectableCallOffs.length > 0 && selectableCallOffs.every((co) => selectedIds.has(co.id));

  const toggleSelectAll = () => {
    if (allSelectableChecked) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableCallOffs.map((co) => co.id)));
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const executeBulkAction = async () => {
    if (!bulkAction || selectedIds.size === 0) return;
    setActionLoading(true);
    try {
      const ids = Array.from(selectedIds);
      if (bulkAction === 'confirm') {
        const res = await callOffAPI.bulkConfirm(ids);
        const confirmed = res.data.confirmed || 0;
        const errors = res.data.errors || [];
        if (errors.length > 0) {
          toast.success(`${confirmed} call-off(s) confirmed, ${errors.length} failed`);
        } else {
          toast.success(`${confirmed} call-off(s) confirmed successfully`);
        }
      } else {
        const promises = ids.map((id) =>
          bulkAction === 'send'
            ? callOffAPI.sendCallOff(id, true)
            : callOffAPI.cancelCallOff(id)
        );
        await Promise.all(promises);
        toast.success(`${ids.length} call-off(s) ${bulkAction === 'send' ? 'sent' : 'cancelled'} successfully`);
      }
      fetchData();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : `Failed to ${bulkAction} call-offs`;
      toast.error(message);
    } finally {
      setActionLoading(false);
      setBulkAction(null);
    }
  };

  const handleSingleSend = async (id: number, skipValidation = false) => {
    try {
      await callOffAPI.sendCallOff(id, skipValidation);
      toast.success('Call-off sent to supplier');
      fetchData();
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { code?: string; message?: string } } };
      if (axiosErr.response?.data?.code === 'insufficient_po_coverage') {
        const override = window.confirm(
          `${axiosErr.response.data.message || 'Insufficient PO coverage.'}\n\nDo you want to send anyway without PO validation?`
        );
        if (override) {
          handleSingleSend(id, true);
          return;
        }
      } else {
        const message = axiosErr.response?.data?.message || (err instanceof Error ? err.message : 'Failed to send call-off');
        toast.error(message);
      }
    }
  };

  const handleSingleCancel = async (id: number) => {
    try {
      await callOffAPI.cancelCallOff(id);
      toast.success('Call-off cancelled');
      fetchData();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to cancel call-off';
      toast.error(message);
    }
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const getBulkActionTitle = () => {
    switch (bulkAction) {
      case 'send': return 'Send Call-Offs';
      case 'cancel': return 'Cancel Call-Offs';
      case 'confirm': return 'Confirm Call-Offs';
      default: return '';
    }
  };

  const getBulkActionDescription = () => {
    switch (bulkAction) {
      case 'send': return `Are you sure you want to send ${selectedIds.size} call-off(s) to the supplier?`;
      case 'cancel': return `Are you sure you want to cancel ${selectedIds.size} call-off(s)? This cannot be undone.`;
      case 'confirm': return `Are you sure you want to confirm ${selectedIds.size} call-off(s)? This will lock them from further editing.`;
      default: return '';
    }
  };

  const getBulkActionConfirmLabel = () => {
    switch (bulkAction) {
      case 'send': return 'Yes, send';
      case 'cancel': return 'Yes, cancel';
      case 'confirm': return 'Yes, confirm';
      default: return 'Confirm';
    }
  };

  return (
    <div className="space-y-4">
      {/* Active / Completed / Cancelled toggle */}
      <div className="flex items-center gap-2">
        <Button
          variant={activeFilter === 'active' ? 'default' : 'outline'}
          size="sm"
          onClick={() => { setActiveFilter('active'); setStatusFilter(''); setPage(1); }}
        >
          Active
        </Button>
        <Button
          variant={activeFilter === 'completed' ? 'default' : 'outline'}
          size="sm"
          onClick={() => { setActiveFilter('completed'); setStatusFilter(''); setPage(1); }}
        >
          Completed
        </Button>
        <Button
          variant={activeFilter === 'cancelled' ? 'default' : 'outline'}
          size="sm"
          onClick={() => { setActiveFilter('cancelled'); setStatusFilter(''); setPage(1); }}
        >
          Cancelled
        </Button>
      </div>

      {/* Stats cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          {Object.entries(STATUS_CONFIG).map(([key, config]) => {
            const count = stats[key as keyof CallOffStats] || 0;
            const isActive = statusFilter === key;
            return (
              <Card
                key={key}
                className={`cursor-pointer hover:border-primary/50 ${isActive ? 'border-primary ring-1 ring-primary/30' : ''}`}
                onClick={() => {
                  if (isActive) {
                    setStatusFilter('');
                  } else {
                    // Auto-switch the toggle so the drill-in actually returns rows.
                    if (key === 'cancelled') setActiveFilter('cancelled');
                    else if (key === 'delivered') setActiveFilter('completed');
                    else setActiveFilter('active');
                    setStatusFilter(key);
                  }
                  setPage(1);
                }}
              >
                <CardContent className="p-3">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">{config.label}</span>
                    <config.icon className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <p className="text-2xl font-bold mt-1">{count}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Filters & Actions */}
      <div className="flex items-center gap-4 flex-wrap">
        <Input
          placeholder="Search by reference, supplier..."
          className="w-64"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
        <Select
          value={statusFilter || 'all'}
          onValueChange={(v) => { setStatusFilter(v === 'all' ? '' : v); setPage(1); }}
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder="All Statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {Object.entries(STATUS_CONFIG).map(([key, config]) => (
              <SelectItem key={key} value={key}>{config.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Supplier filter (customer only) - searchable dropdown */}
        {isCustomer && suppliers.length > 0 && (
          <div className="relative w-52">
            <div className="relative">
              <input
                type="text"
                placeholder="Search suppliers..."
                value={supplierFilter ? selectedSupplierName : supplierSearchTerm}
                onChange={(e) => {
                  setSupplierSearchTerm(e.target.value);
                  setSupplierFilter('');
                  setSupplierDropdownOpen(true);
                }}
                onFocus={() => setSupplierDropdownOpen(true)}
                className="w-full px-3 py-2 pr-8 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <ChevronDown className="absolute right-3 top-2.5 h-4 w-4 text-muted-foreground pointer-events-none" />
              {supplierFilter && (
                <button
                  type="button"
                  onClick={() => {
                    setSupplierFilter('');
                    setSupplierSearchTerm('');
                    setPage(1);
                  }}
                  className="absolute right-8 top-2 p-1 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            {supplierDropdownOpen && filteredSupplierOptions.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-background border border-border rounded-lg shadow-lg max-h-48 overflow-auto">
                {filteredSupplierOptions.slice(0, 50).map(s => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => {
                      setSupplierFilter(String(s.id));
                      setSupplierSearchTerm('');
                      setSupplierDropdownOpen(false);
                      setPage(1);
                    }}
                    className="w-full px-3 py-2 text-left hover:bg-muted text-sm"
                  >
                    {s.name}{s.supplier_code ? ` (${s.supplier_code})` : ''}
                  </button>
                ))}
              </div>
            )}
            {supplierDropdownOpen && (
              <div
                className="fixed inset-0 z-40"
                onClick={() => setSupplierDropdownOpen(false)}
              />
            )}
          </div>
        )}

        {/* Bulk action buttons - Customer */}
        {selectedIds.size > 0 && isCustomer && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">{selectedIds.size} selected</span>
            <Button size="sm" variant="outline" className="gap-1 text-green-700 border-green-300 hover:bg-green-50" onClick={() => setBulkAction('send')}>
              <SendHorizonal className="h-3.5 w-3.5" /> Send
            </Button>
            <Button size="sm" variant="outline" className="gap-1 text-red-700 border-red-300 hover:bg-red-50" onClick={() => setBulkAction('cancel')}>
              <XCircle className="h-3.5 w-3.5" /> Cancel
            </Button>
          </div>
        )}

        {/* Bulk action buttons - Supplier */}
        {selectedIds.size > 0 && !isCustomer && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">{selectedIds.size} selected</span>
            <Button size="sm" variant="outline" className="gap-1 text-green-700 border-green-300 hover:bg-green-50" onClick={() => setBulkAction('confirm')}>
              <CheckCircle className="h-3.5 w-3.5" /> Confirm
            </Button>
          </div>
        )}

        <div className="flex-1" />
        {isCustomer && (
          <Button className="gap-2" onClick={() => setShowNewDialog(true)}>
            <Plus className="h-4 w-4" />
            New Call Off
          </Button>
        )}
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : callOffs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No call-offs found.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    {selectableCallOffs.length > 0 && (
                      <Checkbox
                        checked={allSelectableChecked}
                        onCheckedChange={toggleSelectAll}
                      />
                    )}
                  </TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead>{isCustomer ? 'Supplier' : 'Customer'}</TableHead>
                  <TableHead>Pickup Date</TableHead>
                  <TableHead>Destination</TableHead>
                  <TableHead className="text-right">Fill %</TableHead>
                  <TableHead>Detail Status</TableHead>
                  {activeFilter !== 'cancelled' && <TableHead>Status</TableHead>}
                  <TableHead>Created Date</TableHead>
                  <TableHead>Last Edited</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {callOffs.map((co) => {
                  const statusConf = STATUS_CONFIG[co.detail_status];
                  const isSelectable = co.detail_status === selectableStatus;
                  const fillVal = parseFloat(co.fill_percent || '0');
                  const DestIcon = DESTINATION_ICONS[co.destination_type];

                  return (
                    <TableRow
                      key={co.id}
                      className="cursor-pointer"
                      onClick={() => setSelectedCallOffId(co.id)}
                    >
                      {/* Checkbox */}
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {isSelectable && (
                          <Checkbox
                            checked={selectedIds.has(co.id)}
                            onCheckedChange={() => toggleSelect(co.id)}
                          />
                        )}
                      </TableCell>

                      {/* Reference */}
                      <TableCell className="font-medium">{co.reference}</TableCell>

                      {/* Supplier / Customer */}
                      <TableCell>{isCustomer ? co.supplier_name : co.customer_name}</TableCell>

                      {/* Pickup Date */}
                      <TableCell>{co.pickup_date ? formatDate(co.pickup_date) : '-'}</TableCell>

                      {/* Destination */}
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          {DestIcon && <DestIcon className="h-4 w-4 text-muted-foreground" />}
                          {co.destination_type === 'mixed' ? (
                            <span className="text-purple-700 font-medium">{co.destination_name || 'Mixed Delivery'}</span>
                          ) : (
                            <span>{co.destination_name || '-'}</span>
                          )}
                        </div>
                      </TableCell>

                      {/* Fill % */}
                      <TableCell className={`text-right ${getFillColorClass(fillVal)}`}>
                        {fillVal.toFixed(1)}%
                      </TableCell>

                      {/* Detail Status */}
                      <TableCell>
                        {statusConf && (
                          <Badge variant="secondary" className={statusConf.color}>
                            {statusConf.label}
                          </Badge>
                        )}
                      </TableCell>

                      {/* Status (Active/Completed) — hidden on Cancelled view since the
                          underlying value is 'completed', which would mislead. */}
                      {activeFilter !== 'cancelled' && (
                        <TableCell>
                          {co.status === 'active' ? (
                            <Badge variant="secondary" className="bg-green-100 text-green-700">Active</Badge>
                          ) : (
                            <Badge variant="secondary" className="bg-blue-100 text-blue-700">Completed</Badge>
                          )}
                        </TableCell>
                      )}

                      {/* Created Date */}
                      <TableCell className="text-muted-foreground text-sm">
                        {co.created_at ? formatDate(co.created_at) : '-'}
                      </TableCell>

                      {/* Last Edited */}
                      <TableCell className="text-muted-foreground text-sm">
                        {co.updated_at ? formatDate(co.updated_at) : '-'}
                      </TableCell>

                      {/* Actions */}
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        {co.detail_status === 'draft' && isCustomer ? (
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-green-700 border-green-300 hover:bg-green-50"
                              title="Send to Supplier"
                              onClick={() => handleSingleSend(co.id)}
                            >
                              <SendHorizonal className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-red-700 border-red-300 hover:bg-red-50"
                              title="Cancel"
                              onClick={() => handleSingleCancel(co.id)}
                            >
                              <XCircle className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">Click to view</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {page} of {totalPages} ({totalCount} total)
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Bulk action confirmation dialog */}
      <AlertDialog open={bulkAction !== null} onOpenChange={(open) => { if (!open) setBulkAction(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{getBulkActionTitle()}</AlertDialogTitle>
            <AlertDialogDescription>{getBulkActionDescription()}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionLoading}>No, go back</AlertDialogCancel>
            <AlertDialogAction
              onClick={executeBulkAction}
              disabled={actionLoading}
              className={bulkAction === 'cancel' ? 'bg-red-600 hover:bg-red-700' : ''}
            >
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {getBulkActionConfirmLabel()}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CallOffWizard
        open={showNewDialog || editCallOff !== null}
        onClose={() => { setShowNewDialog(false); setEditCallOff(null); }}
        onCreated={fetchData}
        editCallOff={editCallOff}
      />

      <CallOffDetailModal
        open={selectedCallOffId !== null}
        callOffId={selectedCallOffId}
        onClose={() => {
          setSelectedCallOffId(null);
          if (searchParams.has('calloff_id')) {
            searchParams.delete('calloff_id');
            setSearchParams(searchParams, { replace: true });
          }
        }}
        onUpdated={fetchData}
        onEditInWizard={(co) => {
          setSelectedCallOffId(null);
          setEditCallOff(co);
        }}
      />
    </div>
  );
}
